import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LogIn, X } from 'lucide-react';
import { Select, TextField } from '@components/base/inputs';
import { Modal, Alert, Spinner } from '@components/base/feedback';
import { Button } from '@components/base/actions';
import { Text, Badge, Icon } from '@components/base/display';
import { VStack, HStack } from '@components/base/layout';
import type { TenantSpoofDropdownProps } from './types';

// ---------------------------------------------------------------------------
// Minimal local types for API responses
// ---------------------------------------------------------------------------

interface ErpRegistry {
  id: string;
  slug: string;
  name: string;
  is_main: boolean;
  auth_type: string;
}

interface Tenant {
  id: number;
  slug: string;
  name: string;
}

interface ErpEndpoint {
  id: string;
  slug: string;
}

interface DryRunResponse {
  success: boolean;
  data: unknown;
  error: unknown;
  metadata: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Credential presets
// ---------------------------------------------------------------------------

const CREDENTIAL_PRESETS = [
  { value: 'system-tztufn', label: 'SYSTEM / TZTUFN', uid: 'SYSTEM', passwd: 'TZTUFN' },
];

const PRESET_OPTIONS = [
  { value: '', label: 'Custom...' },
  ...CREDENTIAL_PRESETS.map((p) => ({ value: p.value, label: p.label })),
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TenantSpoofDropdown({ store, apiClient, queryKeyPrefix, layout = 'vertical', onSessionConfirm }: Readonly<TenantSpoofDropdownProps>) {
  const queryClient = useQueryClient();
  const {
    spoofedTenantId,
    spoofedBearerToken: _spoofedBearerToken,
    spoofedSessionId: storeSpoofedSessionId,
    spoofedSessionLabel,
    setSpoofedTenantId,
    setSpoofedBearerToken,
    setSpoofedSessionId: setStoreSpoofedSessionId,
    clearSpoofing,
  } = store;

  const [modalOpen, setModalOpen] = useState(false);
  const [pendingTenantId, setPendingTenantId] = useState<string | null>(null);
  const [uid, setUid] = useState('');
  const [passwd, setPasswd] = useState('');
  const [language, setLanguage] = useState('fr');
  const [selectedPreset, setSelectedPreset] = useState('');
  const [sessionStep, setSessionStep] = useState<'login' | 'session-select'>('login');
  const [sessionList, setSessionList] = useState<Array<Record<string, unknown>>>([]);
  const [sessionListLoading, setSessionListLoading] = useState(false);

  // ---- Fetch tenants -------------------------------------------------------

  const { data: tenantsResponse, isLoading: tenantsLoading } = useQuery({
    queryKey: [queryKeyPrefix, 'tenants'],
    queryFn: async () => {
      const response = await apiClient.get<{ data: Tenant[] }>('/admin/tenants');
      return response.data;
    },
  });

  const tenants = useMemo(
    () => tenantsResponse?.data ?? [],
    [tenantsResponse],
  );

  const tenantOptions = useMemo(
    () => tenants.map((t) => ({ value: String(t.id), label: t.name })),
    [tenants],
  );

  // ---- Fetch registries for the selected tenant ----------------------------

  /* v8 ignore start */
  const pendingTenantSlug = useMemo(() => {
    if (!pendingTenantId) return null;
    const tenant = tenants.find((t) => String(t.id) === pendingTenantId);
    return tenant?.slug ?? null;
  }, [pendingTenantId, tenants]);
  /* v8 ignore stop */

  const { data: tenantRegistriesResponse, isLoading: tenantRegistriesLoading } = useQuery({
    queryKey: [queryKeyPrefix, 'tenants', pendingTenantSlug, 'registries'],
    queryFn: async () => {
      /* v8 ignore start */
      if (!pendingTenantSlug) throw new Error('No tenant selected');
      /* v8 ignore stop */
      const response = await apiClient.get<{ data: ErpRegistry[] }>(
        `/admin/tenants/${pendingTenantSlug}/registries`,
      );
      return response.data;
    },
    enabled: !!pendingTenantSlug,
  });

  const mainLoginBearerRegistry = useMemo(() => {
    const registries = tenantRegistriesResponse?.data ?? [];
    return registries.find(
      (r) => r.is_main && r.auth_type === 'login_bearer',
    ) ?? null;
  }, [tenantRegistriesResponse]);

  // ---- Fetch endpoints for the main login_bearer registry ------------------

  const registrySlug = mainLoginBearerRegistry?.slug;

  const { data: endpointsResponse } = useQuery({
    queryKey: [queryKeyPrefix, 'erp', 'registries', registrySlug, 'endpoints'],
    queryFn: async () => {
      /* v8 ignore start */
      if (!registrySlug) throw new Error('No registry slug');
      /* v8 ignore stop */
      const response = await apiClient.get<{ data: ErpEndpoint[] }>(
        `/admin/erp/registries/${registrySlug}/endpoints`,
      );
      return response.data;
    },
    enabled: !!registrySlug,
  });

  const loginEndpoint = useMemo(() => {
    const endpoints = endpointsResponse?.data ?? [];
    return endpoints.find((e) => e.slug.includes('login')) ?? null;
  }, [endpointsResponse]);

  const sessionListEndpoint = useMemo(() => {
    const endpoints = endpointsResponse?.data ?? [];
    return endpoints.find((e) => e.slug === 'spoofable-session-list') ?? null;
  }, [endpointsResponse]);

  // ---- Helpers for dry run mutation ----------------------------------------

  function extractBearerToken(resData: Record<string, unknown> | undefined): string | null {
    return (
      (resData?.token as string | undefined) ??
      (resData?.bearer_token as string | undefined) ??
      (resData?.access_token as string | undefined) ??
      null
    );
  }

  function buildTokenString(token: string | null, resData: Record<string, unknown> | undefined): string | null {
    return token ?? (resData ? JSON.stringify(resData) : null);
  }

  function fetchSessionList(slug: string, endpointSlug: string, tenantId: string, storedToken: string | null) {
    const headers: Record<string, string> = {};
    if (tenantId) headers['X-Erp-Tenant-Id'] = tenantId;
    if (storedToken) headers['X-Erp-Bearer-Token'] = storedToken;

    apiClient
      .post<DryRunResponse>(
        `/admin/erp/registries/${slug}/endpoints/${endpointSlug}/dry-run`,
        { dry_run: false },
        Object.keys(headers).length > 0 ? { headers } : undefined,
      )
      .then((axiosRes) => {
        const paginated = axiosRes.data.data as { data?: Array<Record<string, unknown>> };
        setSessionList(paginated?.data ?? []);
      })
      .catch(() => {
        setSessionList([]);
      })
      .finally(() => {
        setSessionListLoading(false);
      });
  }

  // ---- Dry run mutation (actual login) -------------------------------------

  const dryRunMutation = useMutation({
    mutationFn: async () => {
      /* v8 ignore start */
      if (!registrySlug || !loginEndpoint || !pendingTenantId) {
        throw new Error('Missing registry, endpoint, or tenant');
      }
      /* v8 ignore stop */
      /* v8 ignore start */
      const headers: Record<string, string> = {};
      if (pendingTenantId) {
        headers['X-Erp-Tenant-Id'] = pendingTenantId;
      }
      const response = await apiClient.post<DryRunResponse>(
        `/admin/erp/registries/${registrySlug}/endpoints/${loginEndpoint.slug}/dry-run`,
        { data: { uid, passwd, language }, dry_run: false },
        Object.keys(headers).length > 0 ? { headers } : undefined,
      );
      return response.data;
      /* v8 ignore stop */
    },
    /* v8 ignore start */
    onSuccess: (res) => {
      if (!pendingTenantId) return;

      const resData = res.data as Record<string, unknown> | undefined;
      const token = extractBearerToken(resData);
      setSpoofedBearerToken(buildTokenString(token, resData));

      if (sessionListEndpoint && registrySlug) {
        setSessionStep('session-select');
        setSessionListLoading(true);
        fetchSessionList(registrySlug, sessionListEndpoint.slug, pendingTenantId, buildTokenString(token, resData));
      } else {
        setSpoofedTenantId(pendingTenantId);
        setModalOpen(false);
      }
    },
    /* v8 ignore stop */
  });

  // ---- Handlers ------------------------------------------------------------

  const handleTenantChange = useCallback(
    (value: string | string[]) => {
      /* v8 ignore start */
      const selectedId = Array.isArray(value) ? value[0] : value;
      if (!selectedId) return;
      /* v8 ignore stop */

      // If the same tenant is already spoofed, do nothing
      if (selectedId === spoofedTenantId) return;

      setPendingTenantId(selectedId);
      setUid('');
      setPasswd('');
      setLanguage('fr');
      setSelectedPreset('');
      dryRunMutation.reset();
      setModalOpen(true);
    },
    [spoofedTenantId, dryRunMutation],
  );

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setPendingTenantId(null);
    dryRunMutation.reset();
    setSessionStep('login');
    setSessionList([]);
    setStoreSpoofedSessionId(null);
    setSessionListLoading(false);
  }, [dryRunMutation, setStoreSpoofedSessionId]);

  const handleSubmit = useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault();
      dryRunMutation.mutate();
    },
    [dryRunMutation],
  );

  const handleStopSpoofing = useCallback(() => {
    clearSpoofing();
    queryClient.clear();
  }, [clearSpoofing, queryClient]);

  // ---- Preset handler -------------------------------------------------------

  const handlePresetChange = useCallback((value: string | string[]) => {
    /* v8 ignore start */
    const selected = Array.isArray(value) ? value[0] : value;
    setSelectedPreset(selected ?? '');
    /* v8 ignore stop */
    const preset = CREDENTIAL_PRESETS.find((p) => p.value === selected);
    if (preset) {
      setUid(preset.uid);
      setPasswd(preset.passwd);
    }
  }, []);

  // ---- Session selection handlers -------------------------------------------

  /* v8 ignore start */
  const handleSessionSelect = useCallback((value: string | string[]) => {
    const selectedId = Array.isArray(value) ? value[0] : value;
    if (!selectedId) return;
    const session = sessionList.find((s) => String(s['session']) === selectedId);
    const label = session ? String(session['lib'] ?? session['session']) : null;
    setStoreSpoofedSessionId(Number(selectedId), label);
  }, [setStoreSpoofedSessionId, sessionList]);
  /* v8 ignore stop */

  const handleSessionConfirm = useCallback(() => {
    setSpoofedTenantId(pendingTenantId);
    if (storeSpoofedSessionId != null && onSessionConfirm) {
      onSessionConfirm(storeSpoofedSessionId);
    }
    setModalOpen(false);
    setSessionStep('login');
    setSessionList([]);
  }, [pendingTenantId, setSpoofedTenantId, storeSpoofedSessionId, onSessionConfirm]);

  /* v8 ignore start */
  const sessionOptions = useMemo(
    () =>
      sessionList.map((s) => ({
        value: String(s['session']),
        label: `${s['lib'] ?? s['session']} (${s['session']})`,
      })),
    [sessionList],
  );
  /* v8 ignore stop */

  // ---- Derived values ------------------------------------------------------

  const spoofedTenantName = useMemo(() => {
    if (!spoofedTenantId) return null;
    const tenant = tenants.find((t) => String(t.id) === spoofedTenantId);
    return tenant?.name ?? null;
  }, [spoofedTenantId, tenants]);

  /* v8 ignore start */
  const pendingTenantName = useMemo(() => {
    if (!pendingTenantId) return null;
    const tenant = tenants.find((t) => String(t.id) === pendingTenantId);
    return tenant?.name ?? null;
  }, [pendingTenantId, tenants]);
  /* v8 ignore stop */

  // ---- Early returns -------------------------------------------------------

  /* v8 ignore next 2 */
  if (tenantsLoading || tenants.length === 0) {
    return null;
  }

  // ---- Render --------------------------------------------------------------

  const isHorizontal = layout === 'horizontal';
  const Wrapper = isHorizontal ? HStack : VStack;

  return (
    <>
      <Wrapper gap={2} align={isHorizontal ? 'center' : undefined}>
        <Select
          label={isHorizontal ? undefined : 'Spoof Tenant'}
          options={tenantOptions}
          value={spoofedTenantId ?? ''}
          onChange={handleTenantChange}
          placeholder="Select tenant..."
          size="sm"
          searchable
        />

        {spoofedTenantName && (
          <HStack gap={1} align="center" className="shrink-0">
            <Badge variant="success" size="sm" className="whitespace-nowrap">
              {spoofedSessionLabel ?? spoofedTenantName}{storeSpoofedSessionId != null && !spoofedSessionLabel ? ` #${storeSpoofedSessionId}` : ''}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleStopSpoofing}
              leftIcon={<Icon icon={X} size="sm" />}
              aria-label="Stop spoofing"
            />
          </HStack>
        )}
      </Wrapper>

      {/* v8 ignore start */}
      <Modal open={modalOpen} onClose={handleModalClose} size="sm">
        {sessionStep === 'login' ? (
          <>
            <Modal.Header title={`Login to ${pendingTenantName ?? 'tenant'}`} />
            <form onSubmit={handleSubmit}>
              <Modal.Body>
                <VStack gap={4}>
                  <Text size="sm" color="muted">
                    Authenticate to spoof this tenant. Credentials are sent to the
                    ERP login endpoint.
                  </Text>

                  <Select
                    label="Preset"
                    options={PRESET_OPTIONS}
                    value={selectedPreset}
                    onChange={handlePresetChange}
                    size="sm"
                  />

                  <TextField
                    label="UID"
                    value={uid}
                    onChange={(e) => {
                      setUid(e.target.value);
                    }}
                    size="sm"
                    required
                  />

                  <TextField
                    label="Password"
                    type="password"
                    value={passwd}
                    onChange={(e) => {
                      setPasswd(e.target.value);
                    }}
                    size="sm"
                    required
                  />

                  <TextField
                    label="Language"
                    value={language}
                    onChange={(e) => {
                      setLanguage(e.target.value);
                    }}
                    size="sm"
                    required
                  />

                  {!tenantRegistriesLoading && pendingTenantId && !mainLoginBearerRegistry && (
                    <Alert variant="error" title="No ERP registry">
                      This tenant has no main login_bearer registry configured.
                    </Alert>
                  )}

                  {dryRunMutation.isError && (
                    <Alert variant="error" title="Login failed">
                      {dryRunMutation.error instanceof Error
                        ? dryRunMutation.error.message
                        : 'An unexpected error occurred.'}
                    </Alert>
                  )}
                </VStack>
              </Modal.Body>
              <Modal.Footer>
                <HStack gap={2} justify="end">
                  <Button
                    variant="secondary"
                    onClick={handleModalClose}
                    type="button"
                    size="sm"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    loading={dryRunMutation.isPending || tenantRegistriesLoading}
                    disabled={!loginEndpoint}
                    leftIcon={<Icon icon={LogIn} size="sm" />}
                    size="sm"
                  >
                    Login
                  </Button>
                </HStack>
              </Modal.Footer>
            </form>
          </>
        ) : /* v8 ignore start */ (
          <>
            <Modal.Header title={`Select session for ${pendingTenantName ?? 'tenant'}`} />
            <Modal.Body className="min-h-[300px]">
              {sessionListLoading ? /* v8 ignore stop */ (
                <VStack gap={4} align="center">
                  <Spinner size="md" />
                  <Text size="sm" color="muted">Loading sessions...</Text>
                </VStack>
              ) : (
                <VStack gap={4}>
                  <Text size="sm" color="muted">
                    Select a session to spoof for this tenant.
                  </Text>
                  <Select
                    label="Session"
                    options={sessionOptions}
                    value={storeSpoofedSessionId == null ? '' : String(storeSpoofedSessionId)}
                    onChange={handleSessionSelect}
                    placeholder="Select session..."
                    searchable
                    size="sm"
                  />
                </VStack>
              )}
            </Modal.Body>
            <Modal.Footer>
              <HStack gap={2} justify="end">
                <Button
                  variant="secondary"
                  onClick={handleModalClose}
                  size="sm"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSessionConfirm}
                  disabled={storeSpoofedSessionId == null}
                  size="sm"
                >
                  Confirm
                </Button>
              </HStack>
            </Modal.Footer>
          </>
        )}
      </Modal>
      {/* v8 ignore stop */}
    </>
  );
}
